/**
 * Assessment authoring store — flat (root) mutations so the existing
 * Authoring.vue continues to use `this.$store.commit('ADD_QUESTION', ...)`
 * without a namespace prefix.
 *
 * Persists the work-in-progress assessment to localStorage to survive reloads.
 */
const ASSESSMENT_KEY = 'mu.assessment';

/**
 * Tipos de pregunta soportados (13). El editor usa estos enums directamente,
 * que coinciden con los del backend salvo los alias 'single'/'multi'.
 */
export const QUESTION_TYPES = [
  { value: 'single_choice', label: 'Single choice', short: 'SC' },
  { value: 'multiple_choice', label: 'Multiple choice', short: 'MC' },
  { value: 'fill_blank', label: 'Fill in the blank', short: 'FB' },
  { value: 'reorder_list', label: 'Reorder list', short: 'RO' },
  { value: 'build_list', label: 'Build list', short: 'BL' },
  { value: 'build_reorder', label: 'Build & reorder', short: 'BR' },
  { value: 'tree', label: 'Tree', short: 'TR' },
  { value: 'pin', label: 'Pin (hotspot)', short: 'PN' },
  { value: 'select_place', label: 'Select & place', short: 'SP' },
  { value: 'content_table', label: 'Content table', short: 'CT' },
  { value: 'command_line', label: 'Command line', short: 'CL' },
  { value: 'live_screen', label: 'Live screen', short: 'LS' },
  { value: 'case_study', label: 'Case study', short: 'CS' },
];

/**
 * Normaliza los alias del editor original ('single'/'multi') al enum canónico.
 */
export function normalizeType(type) {
  if (type === 'single') return 'single_choice';
  if (type === 'multi') return 'multiple_choice';
  return type || 'single_choice';
}

/**
 * Mapea el tipo del editor (single/multi) al enum del backend y viceversa.
 */
export function editorTypeToApi(type) {
  return normalizeType(type);
}
export function apiTypeToEditor(type) {
  return type || 'single_choice';
}

/**
 * Construye el objeto `data` (el JSON que consume el runner) a partir de la
 * pregunta del editor, según su tipo. Cada tipo tiene su propio esquema.
 */
export function buildQuestionData(q) {
  const type = normalizeType(q.type);
  const d = q.data || {};
  const base = { points: Number(q.points) || 1 };
  if (Array.isArray(d.references) && d.references.length) {
    base.references = d.references;
  }

  switch (type) {
    case 'single_choice':
      return {
        ...base,
        options: (q.options || []).map((o) => ({ id: o.id, text: o.text, correct: !!o.correct })),
      };
    case 'multiple_choice':
      return {
        ...base,
        options: (q.options || []).map((o) => ({ id: o.id, text: o.text, correct: !!o.correct })),
        select_count: d.select_count || null,
      };
    case 'fill_blank':
      return {
        ...base,
        blanks: (d.blanks || []).map((b) => ({ id: b.id, placeholder: b.placeholder || '', width: b.width || null, correct: b.correct || [] })),
      };
    case 'reorder_list':
    case 'build_reorder':
      return {
        ...base,
        items: (d.items || []).map((i) => ({ id: i.id, text: i.text })),
        correct_order: (d.items || []).filter((i) => i.inAnswer !== false).map((i) => i.id),
      };
    case 'build_list':
      return {
        ...base,
        items: (d.items || []).map((i) => ({ id: i.id, text: i.text })),
        correct_selected: (d.items || []).filter((i) => i.correct).map((i) => i.id),
      };
    case 'tree':
      return {
        ...base,
        mode: d.mode || 'checkbox',
        tree: d.tree || [],
        correct: collectCorrectTreeIds(d.tree || []),
      };
    case 'pin':
      return {
        ...base,
        image_url: d.image_url || '',
        max_pins: d.max_pins || 1,
        targets: (d.targets || []).map((t) => ({ x: t.x, y: t.y, radius: t.radius || 8 })),
      };
    case 'select_place':
      return {
        ...base,
        image_url: d.image_url || '',
        options: (d.options || []).map((o) => ({ id: o.id, text: o.text })),
        targets: (d.targets || []).map((t) => ({ id: t.id, x: t.x, y: t.y, label: t.label || '', correct: t.correct || null })),
      };
    case 'content_table':
      return {
        ...base,
        columns: (d.columns || []).map((c) => ({ id: c.id, label: c.label, type: c.type || 'radio', options: c.options || [] })),
        rows: (d.rows || []).map((r) => ({ ...r })),
        correct: d.correct || {},
      };
    case 'command_line':
      return {
        ...base,
        terminal_title: d.terminal_title || 'bash',
        prompt: d.prompt || '$ ',
        responses: d.responses || {},
        default_response: d.default_response || '',
        expected_commands: d.expected_commands || [],
      };
    case 'live_screen':
      return {
        ...base,
        environment_base_url: d.environment_base_url || '',
      };
    case 'case_study':
      return {
        ...base,
        scenario_title: d.scenario_title || '',
        scenario: d.scenario || '',
        exhibit_url: d.exhibit_url || '',
        exhibit_label: d.exhibit_label || '',
        sub_questions: (d.sub_questions || []).map((s) => ({
          id: s.id,
          type: s.type || 'single_choice',
          stem: s.stem || '',
          data: s.data || { options: [] },
        })),
      };
    default:
      return base;
  }
}

function collectCorrectTreeIds(nodes, acc = []) {
  for (const n of nodes) {
    if (n.correct) acc.push(n.id);
    if (n.children && n.children.length) collectCorrectTreeIds(n.children, acc);
  }
  return acc;
}

/**
 * Serializa una pregunta del editor al payload del backend (campo data JSON).
 */
export function questionToApiPayload(q) {
  return {
    type: normalizeType(q.type),
    text: q.prompt || '',
    explanation: q.explanation || '',
    difficulty: q.difficulty || 'medium',
    bloom_taxonomy: q.bloomTaxonomy || null,
    competency_id: q.competencyId || null,
    references_enabled: !!q.referencesEnabled,
    tag_names: Array.isArray(q.tags) ? q.tags.filter(Boolean) : [],
    data: buildQuestionData(q),
  };
}

/**
 * Deserializa una pregunta del backend al shape del editor.
 */
export function apiQuestionToEditor(row) {
  const data = row.data || {};
  return {
    id: row.id,
    backendId: row.id,
    type: apiTypeToEditor(row.type),
    prompt: row.text || '',
    explanation: row.explanation || '',
    difficulty: row.difficulty || 'medium',
    bloomTaxonomy: row.bloom_taxonomy || '',
    competencyId: row.competency_id || null,
    referencesEnabled: !!row.references_enabled,
    points: typeof data.points === 'number' ? data.points : 1,
    tags: Array.isArray(row.tags) ? row.tags.map((t) => t.name) : [],
    options: (data.options || []).map((o) => ({ id: o.id, text: o.text || '', correct: !!o.correct })),
    data,
  };
}


export function uid(prefix) {
  return prefix + '_' + Math.random().toString(36).slice(2, 10);
}

/**
 * Scaffold del objeto `data` por tipo, con ids estables ya generados.
 */
export function makeBlankData(type) {
  const t = normalizeType(type);
  switch (t) {
    case 'fill_blank':
      return { blanks: [{ id: uid('bl'), placeholder: '', width: null, correct: [''] }] };
    case 'reorder_list':
    case 'build_reorder':
      return { items: [{ id: uid('it'), text: '', inAnswer: true }, { id: uid('it'), text: '', inAnswer: true }] };
    case 'build_list':
      return { items: [{ id: uid('it'), text: '', correct: true }, { id: uid('it'), text: '', correct: false }] };
    case 'tree':
      return { mode: 'checkbox', tree: [{ id: uid('nd'), label: '', correct: false, children: [] }] };
    case 'pin':
      return { image_url: '', max_pins: 1, targets: [] };
    case 'select_place':
      return { image_url: '', options: [{ id: uid('op'), text: '' }], targets: [{ id: uid('tg'), x: 50, y: 50, label: '', correct: null }] };
    case 'content_table':
      return {
        columns: [{ id: uid('col'), label: 'Statement', type: 'label' }, { id: uid('col'), label: 'True', type: 'radio' }, { id: uid('col'), label: 'False', type: 'radio' }],
        rows: [{ id: uid('row') }],
        correct: {},
      };
    case 'command_line':
      return { terminal_title: 'bash', prompt: '$ ', responses: {}, default_response: '', expected_commands: [''] };
    case 'live_screen':
      return { environment_base_url: '' };
    case 'case_study':
      return { scenario_title: '', scenario: '', exhibit_url: '', exhibit_label: '', sub_questions: [] };
    default:
      return {};
  }
}

export function makeBlankQuestion(type = 'single_choice') {
  const t = normalizeType(type);
  const isChoice = t === 'single_choice' || t === 'multiple_choice';
  return {
    id: uid('q'),
    type: t,
    prompt: '',
    explanation: '',
    difficulty: 'medium',
    bloomTaxonomy: '',
    competencyId: null,
    referencesEnabled: false,
    points: 1,
    tags: [],
    options: isChoice
      ? [
          { id: uid('opt'), text: '', correct: t === 'single_choice' },
          { id: uid('opt'), text: '', correct: false },
        ]
      : [],
    data: makeBlankData(t),
  };
}

export function makeBlankAssessment() {
  return {
    id: uid('as'),
    title: 'Untitled assessment',
    description: '',
    passingScore: 70,
    maxAttempts: 3,
    timeLimitMin: 60,
    questionCount: null,
    perQuestionWeighting: false,
    questions: [],
  };
}

function loadAssessment() {
  try {
    const raw = localStorage.getItem(ASSESSMENT_KEY);
    if (!raw) return makeBlankAssessment();
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.questions)) return makeBlankAssessment();
    return parsed;
  } catch {
    return makeBlankAssessment();
  }
}

function persistAssessment(a) {
  try { localStorage.setItem(ASSESSMENT_KEY, JSON.stringify(a)); } catch (_e) {}
}

export const assessmentState = () => ({
  assessment: loadAssessment(),
  ui: {
    previewOpen: false,
    qtiImportOpen: false,
    aiGenerateOpen: false,
  },
});

export const assessmentMutations = {
  OPEN_PREVIEW(state)  { state.ui.previewOpen = true; },
  CLOSE_PREVIEW(state) { state.ui.previewOpen = false; },
  OPEN_QTI_IMPORT(state)  { state.ui.qtiImportOpen = true; },
  CLOSE_QTI_IMPORT(state) { state.ui.qtiImportOpen = false; },
  OPEN_AI_GENERATE(state)  { state.ui.aiGenerateOpen = true; },
  CLOSE_AI_GENERATE(state) { state.ui.aiGenerateOpen = false; },

  UPDATE_ASSESSMENT(state, patch) {
    state.assessment = { ...state.assessment, ...patch };
    persistAssessment(state.assessment);
  },
  RESET_ASSESSMENT(state) {
    state.assessment = makeBlankAssessment();
    persistAssessment(state.assessment);
  },
  LOAD_ASSESSMENT(state, assessment) {
    state.assessment = { ...makeBlankAssessment(), ...assessment };
    persistAssessment(state.assessment);
  },
  ADD_QUESTION(state, type = 'single') {
    const q = makeBlankQuestion(type);
    state.assessment = { ...state.assessment, questions: [...state.assessment.questions, q] };
    persistAssessment(state.assessment);
  },
  DUPLICATE_QUESTION(state, index) {
    const src = state.assessment.questions[index];
    if (!src) return;
    const copy = JSON.parse(JSON.stringify(src));
    copy.id = uid('q');
    delete copy.backendId;
    copy.options = (copy.options || []).map((o) => ({ ...o, id: uid('opt') }));
    const next = state.assessment.questions.slice();
    next.splice(index + 1, 0, copy);
    state.assessment = { ...state.assessment, questions: next };
    persistAssessment(state.assessment);
  },
  DELETE_QUESTION(state, index) {
    const next = state.assessment.questions.slice();
    next.splice(index, 1);
    state.assessment = { ...state.assessment, questions: next };
    persistAssessment(state.assessment);
  },
  UPDATE_QUESTION(state, { index, patch }) {
    const next = state.assessment.questions.slice();
    if (!next[index]) return;
    next[index] = { ...next[index], ...patch };
    state.assessment = { ...state.assessment, questions: next };
    persistAssessment(state.assessment);
  },
  SET_QUESTION_BACKEND_ID(state, { id, backendId }) {
    const idx = state.assessment.questions.findIndex((q) => q.id === id);
    if (idx < 0) return;
    const next = state.assessment.questions.slice();
    next[idx] = { ...next[idx], backendId };
    state.assessment = { ...state.assessment, questions: next };
    persistAssessment(state.assessment);
  },
  SET_QUESTION_DATA(state, { index, data }) {
    const q = state.assessment.questions[index];
    if (!q) return;
    const next = state.assessment.questions.slice();
    next[index] = { ...q, data: { ...(q.data || {}), ...data } };
    state.assessment = { ...state.assessment, questions: next };
    persistAssessment(state.assessment);
  },
  SET_QUESTION_TYPE(state, { index, type }) {
    const q = state.assessment.questions[index];
    if (!q) return;
    const t = normalizeType(type);
    if (q.type === t) return;
    const isChoice = t === 'single_choice' || t === 'multiple_choice';
    let options = q.options || [];
    if (isChoice) {
      if (options.length < 2) {
        options = [
          { id: uid('opt'), text: '', correct: t === 'single_choice' },
          { id: uid('opt'), text: '', correct: false },
        ];
      } else if (t === 'single_choice') {
        let firstCorrect = options.findIndex((o) => o.correct);
        if (firstCorrect < 0) firstCorrect = 0;
        options = options.map((o, i) => ({ ...o, correct: i === firstCorrect }));
      }
    }
    // Al cambiar a un tipo no-choice, generamos su scaffold de data.
    const data = isChoice ? (q.data || {}) : makeBlankData(t);
    const next = state.assessment.questions.slice();
    next[index] = { ...q, type: t, options, data };
    state.assessment = { ...state.assessment, questions: next };
    persistAssessment(state.assessment);
  },
  REORDER_QUESTIONS(state, questions) {
    state.assessment = { ...state.assessment, questions: questions.slice() };
    persistAssessment(state.assessment);
  },
  ADD_OPTION(state, { qIndex }) {
    const q = state.assessment.questions[qIndex];
    if (!q) return;
    const options = [...q.options, { id: uid('opt'), text: '', correct: false }];
    const next = state.assessment.questions.slice();
    next[qIndex] = { ...q, options };
    state.assessment = { ...state.assessment, questions: next };
    persistAssessment(state.assessment);
  },
  SET_QUESTION_OPTIONS(state, { qIndex, options }) {
    const q = state.assessment.questions[qIndex];
    if (!q) return;
    const normalized = (options || []).map((o) => ({
      id: o.id || uid('opt'),
      text: o.text || '',
      correct: !!o.correct,
    }));
    const next = state.assessment.questions.slice();
    next[qIndex] = { ...q, options: normalized };
    state.assessment = { ...state.assessment, questions: next };
    persistAssessment(state.assessment);
  },
  DELETE_OPTION(state, { qIndex, oIndex }) {
    const q = state.assessment.questions[qIndex];
    if (!q || q.options.length <= 2) return;
    const options = q.options.slice();
    options.splice(oIndex, 1);
    if (q.type === 'single_choice' && !options.some((o) => o.correct) && options.length) {
      options[0] = { ...options[0], correct: true };
    }
    const next = state.assessment.questions.slice();
    next[qIndex] = { ...q, options };
    state.assessment = { ...state.assessment, questions: next };
    persistAssessment(state.assessment);
  },
  UPDATE_OPTION(state, { qIndex, oIndex, patch }) {
    const q = state.assessment.questions[qIndex];
    if (!q || !q.options[oIndex]) return;
    const options = q.options.slice();
    options[oIndex] = { ...options[oIndex], ...patch };
    const next = state.assessment.questions.slice();
    next[qIndex] = { ...q, options };
    state.assessment = { ...state.assessment, questions: next };
    persistAssessment(state.assessment);
  },
  SET_OPTION_CORRECT_SINGLE(state, { qIndex, oIndex }) {
    const q = state.assessment.questions[qIndex];
    if (!q) return;
    const options = q.options.map((o, i) => ({ ...o, correct: i === oIndex }));
    const next = state.assessment.questions.slice();
    next[qIndex] = { ...q, options };
    state.assessment = { ...state.assessment, questions: next };
    persistAssessment(state.assessment);
  },
  IMPORT_QUESTIONS(state, items) {
    const incoming = (items || []).map((q) => {
      const t = normalizeType(q.type);
      return {
        id: q.id || uid('q'),
        type: t,
        prompt: q.prompt || '',
        explanation: q.explanation || '',
        difficulty: q.difficulty || 'medium',
        bloomTaxonomy: q.bloomTaxonomy || '',
        competencyId: q.competencyId || null,
        referencesEnabled: !!q.referencesEnabled,
        points: typeof q.points === 'number' ? q.points : 1,
        tags: Array.isArray(q.tags) ? q.tags : [],
        options: (q.options || []).map((o) => ({
          id: o.id || uid('opt'),
          text: o.text || '',
          correct: !!o.correct,
        })),
        data: q.data || makeBlankData(t),
      };
    });
    state.assessment = { ...state.assessment, questions: [...state.assessment.questions, ...incoming] };
    persistAssessment(state.assessment);
  },
};
